import { useEffect, useRef, useState } from "react";
import { Bookmark, Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import type { SavedView } from "@/hooks/useSavedViews";

interface SavedViewsMenuProps<TFilters> {
  views: SavedView<TFilters>[];
  activeView: SavedView<TFilters> | null;
  hasUnsavedChanges: boolean;
  isDefault: boolean;
  onApply: (id: string) => void;
  onSave: (name: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onUpdateActive: () => void;
  onClear: () => void;
}

/**
 * Dropdown UI for managing saved views on a list page. Sits next to the
 * page's existing filter controls and is fully controlled — all state
 * lives in the parent's `useSavedViews` hook so filter-apply goes
 * through the page's own setters.
 */
export function SavedViewsMenu<TFilters>({
  views,
  activeView,
  hasUnsavedChanges,
  isDefault,
  onApply,
  onSave,
  onDelete,
  onRename,
  onUpdateActive,
  onClear,
}: SavedViewsMenuProps<TFilters>) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSaving(false);
        setRenamingId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave(name.trim());
    setName("");
    setSaving(false);
  };

  const handleRename = (id: string) => {
    if (!renameValue.trim()) return;
    onRename(id, renameValue.trim());
    setRenamingId(null);
    setRenameValue("");
  };

  const label = activeView
    ? hasUnsavedChanges
      ? `${activeView.name} (modified)`
      : activeView.name
    : "Views";

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Bookmark className="h-4 w-4" />
        <span className="max-w-[160px] truncate">{label}</span>
      </Button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 w-72 rounded-xl border border-border bg-surface shadow-lg"
        >
          {views.length === 0 && !saving && (
            <div className="p-4 text-xs text-text-muted">
              No saved views yet. Apply some filters and save a preset —
              you'll jump back with one click.
            </div>
          )}

          {views.length > 0 && (
            <ul className="max-h-64 overflow-auto py-2">
              {views.map((v) => {
                const active = activeView?.id === v.id;
                const renaming = renamingId === v.id;
                return (
                  <li key={v.id} className="px-2">
                    {renaming ? (
                      <div className="flex items-center gap-1 py-1">
                        <Input
                          autoFocus
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleRename(v.id);
                            if (e.key === "Escape") setRenamingId(null);
                          }}
                        />
                        <button
                          onClick={() => handleRename(v.id)}
                          className="p-1 rounded hover:bg-background"
                          aria-label="Confirm rename"
                        >
                          <Check className="h-3.5 w-3.5 text-accent" />
                        </button>
                        <button
                          onClick={() => setRenamingId(null)}
                          className="p-1 rounded hover:bg-background"
                          aria-label="Cancel rename"
                        >
                          <X className="h-3.5 w-3.5 text-text-muted" />
                        </button>
                      </div>
                    ) : (
                      <div
                        className={`group flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm ${
                          active
                            ? "bg-accent/10 text-accent"
                            : "text-text-primary hover:bg-background"
                        }`}
                      >
                        <button
                          onClick={() => {
                            onApply(v.id);
                            setOpen(false);
                          }}
                          className="flex-1 min-w-0 text-left truncate"
                        >
                          {v.name}
                        </button>
                        <button
                          onClick={() => {
                            setRenamingId(v.id);
                            setRenameValue(v.name);
                          }}
                          className="p-1 rounded hover:bg-surface opacity-0 group-hover:opacity-100"
                          aria-label={`Rename ${v.name}`}
                        >
                          <Pencil className="h-3.5 w-3.5 text-text-muted" />
                        </button>
                        <button
                          onClick={() => onDelete(v.id)}
                          className="p-1 rounded hover:bg-surface opacity-0 group-hover:opacity-100"
                          aria-label={`Delete ${v.name}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-danger" />
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <div className="border-t border-border p-2 space-y-1">
            {saving ? (
              <div className="flex items-center gap-1">
                <Input
                  autoFocus
                  placeholder="View name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave();
                    if (e.key === "Escape") {
                      setSaving(false);
                      setName("");
                    }
                  }}
                />
                <button
                  onClick={handleSave}
                  disabled={!name.trim()}
                  className="p-1.5 rounded hover:bg-background disabled:opacity-50"
                  aria-label="Save view"
                >
                  <Check className="h-4 w-4 text-accent" />
                </button>
                <button
                  onClick={() => {
                    setSaving(false);
                    setName("");
                  }}
                  className="p-1.5 rounded hover:bg-background"
                  aria-label="Cancel save"
                >
                  <X className="h-4 w-4 text-text-muted" />
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setSaving(true)}
                  disabled={isDefault}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-text-primary hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
                  title={
                    isDefault
                      ? "Apply some filters before saving a view"
                      : undefined
                  }
                >
                  <Plus className="h-4 w-4" />
                  Save current as view
                </button>
                {activeView && hasUnsavedChanges && (
                  <button
                    onClick={() => {
                      onUpdateActive();
                      setOpen(false);
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-text-primary hover:bg-background"
                  >
                    <Check className="h-4 w-4" />
                    Update "{activeView.name}"
                  </button>
                )}
                {!isDefault && (
                  <button
                    onClick={() => {
                      onClear();
                      setOpen(false);
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-text-muted hover:bg-background"
                  >
                    <X className="h-4 w-4" />
                    Clear filters
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
